/**
 * Testo delle email in ingresso. I client di posta spezzano il testo semplice
 * con un a capo «fisico» ogni ~70 caratteri (hard wrap): reso cosi com'e nel
 * thread (`white-space: pre-wrap`) il messaggio diventa frastagliato. Qui si
 * riuniscono le righe spezzate SOLO dove l'euristica e sicura; tutto il resto
 * (paragrafi, elenchi, citazioni, firme, indirizzi, URL) resta com'e.
 *
 * Il testo salvato non cambia mai: la riunione e solo di visualizzazione, e la
 * modifica di un messaggio lavora sempre sul testo originale.
 */
import type { HistoryEntry, ThreadMessage } from '../../../services/supportApi';

/** Sotto questa lunghezza una riga e «corta» (indirizzi, firme, saluti). */
const LONG_LINE = 50;
/** In alternativa conta la proporzione con la riga piu lunga del paragrafo. */
const LONG_RATIO = 0.7;
/** Il criterio proporzionale vale solo per paragrafi con righe davvero lunghe. */
const RATIO_MIN_BLOCK = 40;
/** Lunghezza minima di una riga flowed (spazio finale) per riunirla sempre. */
const FLOWED_MIN = 30;

const TERMINAL = /[.:;!?)]$/;
const LIST_ITEM = /^(?:[-*•–]\s|\d{1,3}[.)](?:\s|$))/;
const QUOTE = /^\s*>/;
const SIG_DELIM = /^-- ?$/;
const SEPARATOR = /^(?:-{2,}|_{2,}|={2,})/;
const SIGNOFF = /^(?:cordiali saluti|distinti saluti|cordialmente|saluti|un saluto|grazie|buona giornata|buon lavoro|best regards|kind regards|regards|inviato da)\b/i;
const URL_START = /^(?:https?:\/\/|www\.)/i;
const URL_END = /(?:https?:\/\/|www\.)\S*$/i;

const isBlank = (line: string) => line.trim() === '';

const isLong = (line: string, blockMax: number): boolean => {
  const len = line.trimEnd().length;
  return len >= LONG_LINE || (blockMax >= RATIO_MIN_BLOCK && len >= LONG_RATIO * blockMax);
};

/** Colonne allineate (tabella in testo semplice): due o piu spazi, o un tab, fra due parole. */
const HAS_COLUMNS = /\S(?: {2,}|\t)\S/;
/** Elenco di valori brevi (targhe, telai, importi): righe fino a questa lunghezza, con una cifra. */
const SHORT_VALUE = 30;
/** Una riga di chiusura e corta: oltre e una frase che comincia con «Grazie…». */
const SIGNOFF_MAX = 40;

const firstWord = (line: string): string => line.trim().split(/\s+/)[0] || '';
const shapeOf = (word: string): string => word.replace(/\d/g, '9').replace(/[^9]/g, 'A');

/** Riga di chiusura («Cordiali saluti», «Grazie», …): da qui in poi il testo resta com'e. */
const isSignoff = (line: string): boolean => SIGNOFF.test(line.trim()) && line.trim().length <= SIGNOFF_MAX;

/**
 * Due righe consecutive di un elenco di valori, uno per riga: entrambe con una
 * cifra e corte, oppure con la stessa prima parola o la stessa forma della
 * prima parola (targhe AB123CD / EF456GH). La prosa spezzata dal client non ha
 * questa forma.
 */
const homogeneous = (a: string | undefined, b: string | undefined): boolean => {
  if (a === undefined || b === undefined || isBlank(a) || isBlank(b)) return false;
  const ta = a.trim();
  const tb = b.trim();
  if (!/\d/.test(ta) || !/\d/.test(tb)) return false;
  if (ta.length <= SHORT_VALUE && tb.length <= SHORT_VALUE) return true;
  const fa = firstWord(ta);
  const fb = firstWord(tb);
  return fa.toLowerCase() === fb.toLowerCase() || (/\d/.test(fa) && shapeOf(fa) === shapeOf(fb));
};

/** La riga `next` comincia qualcosa di nuovo: non va attaccata alla precedente. */
const startsNewThing = (next: string): boolean =>
  /^[ \t]/.test(next) ||
  LIST_ITEM.test(next) ||
  QUOTE.test(next) ||
  SIG_DELIM.test(next) ||
  SEPARATOR.test(next) ||
  SIGNOFF.test(next) ||
  URL_START.test(next);

const shouldJoin = (cur: string, next: string, blockMax: number): boolean => {
  if (isBlank(cur) || isBlank(next)) return false;
  if (QUOTE.test(cur) || SIG_DELIM.test(cur) || SEPARATOR.test(cur)) return false;
  if (QUOTE.test(next)) return false;
  if (HAS_COLUMNS.test(cur.trim()) || HAS_COLUMNS.test(next.trim())) return false;
  // format=flowed (RFC 3676): lo spazio finale dichiara la riga "a capo morbido".
  // Una riga corta con uno spazio finale e piu spesso un refuso (firma,
  // indirizzo) che un a capo morbido: il flowed vale da FLOWED_MIN caratteri.
  // Anche in flowed un elenco, un separatore o un saluto (tolto lo
  // space-stuffing) restano su una riga propria.
  if (/ $/.test(cur) && cur.trim().length >= FLOWED_MIN) return !startsNewThing(next.replace(/^ /, ''));
  if (startsNewThing(next)) return false;
  const trimmed = cur.trimEnd();
  if (TERMINAL.test(trimmed)) return false;
  if (URL_END.test(trimmed)) return false;
  if (!isLong(cur, blockMax)) return false;
  // Test del reflow: il client va a capo solo se la parola successiva non ci
  // stava. Se ci sarebbe entrata (larghezza stimata = riga piu lunga del
  // paragrafo), l'a capo e voluto.
  return trimmed.length + 1 + firstWord(next).length > blockMax;
};

/**
 * Riunisce le righe spezzate dal client di posta. Funzione pura: a parita di
 * ingresso stessa uscita, e se non c'e nulla da riunire restituisce la stringa
 * ricevuta (stesso riferimento).
 */
export const unwrapEmailText = (text: string): string => {
  if (!text || text.indexOf('\n') === -1) return text;
  const lines = text.replace(/\r\n?/g, '\n').split('\n');

  // Riga piu lunga di ciascun paragrafo (blocco fra righe vuote), citazioni escluse.
  const blockMax: number[] = new Array(lines.length).fill(0);
  let start = 0;
  for (let i = 0; i <= lines.length; i++) {
    if (i === lines.length || isBlank(lines[i])) {
      let max = 0;
      for (let j = start; j < i; j++) if (!QUOTE.test(lines[j])) max = Math.max(max, lines[j].trimEnd().length);
      for (let j = start; j < i; j++) blockMax[j] = max;
      start = i + 1;
    }
  }
  // Righe di un elenco di valori: non si uniscono ne alla precedente ne alla successiva.
  const inValueList = lines.map((l, i) => homogeneous(lines[i - 1], l) || homogeneous(l, lines[i + 1]));

  const out: string[] = [];
  let changed = false;
  let inSignature = false;
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    // Dal separatore "-- " o da un saluto di chiusura in poi il testo resta com'e.
    if (SIG_DELIM.test(line) || isSignoff(line)) inSignature = true;
    // Il criterio guarda sempre l'ULTIMA riga fisica della catena, non la
    // riga gia riunita (che sarebbe "lunga" per costruzione).
    let last = i;
    while (
      !inSignature &&
      last + 1 < lines.length &&
      !inValueList[last] &&
      !inValueList[last + 1] &&
      shouldJoin(lines[last], lines[last + 1], blockMax[last])
    ) {
      const next = lines[last + 1];
      line = / $/.test(lines[last])
        ? line.replace(/ +$/, ' ') + next.replace(/^ /, '') // flowed: un solo spazio, via lo space-stuffing
        : `${line.trimEnd()} ${next}`;
      last += 1;
      changed = true;
    }
    i = last;
    out.push(line);
  }
  return changed ? out.join('\n') : text;
};

/** Distanza massima fra il messaggio e la voce di storico `email_received`. */
const EMAIL_MATCH_MS = 5000;

const timeOf = (iso?: string): number => {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(t) ? NaN : t;
};

/**
 * Quali messaggi del thread pubblico sono arrivati per email. Il messaggio non
 * porta il canale di arrivo, quindi si ricava:
 * - `via === 'email'`, quando il backend lo dice (vista del richiedente);
 * - il primo messaggio di un ticket nato da email (`origin: 'email'`);
 * - le risposte del richiedente scritte nello stesso istante di una voce di
 *   storico `email_received` (le scrive `replyAsRequester` nella stessa update).
 * Note interne e messaggi del canale non sono mai "email".
 */
export const emailMessageFlags = (
  publicThread: Array<Pick<ThreadMessage, 'authorType' | 'sentAt'> & { via?: string; merged?: boolean }>,
  ticket: { origin?: string; history?: Array<Pick<HistoryEntry, 'type' | 'at'>> } = {}
): boolean[] => {
  const thread = publicThread || [];
  const flags = thread.map((m, i) => {
    if (m.via === 'email') return true;
    const fromRequester = m.authorType === 'associate' || m.authorType === 'guest';
    return i === 0 && fromRequester && !m.merged && ticket.origin === 'email';
  });
  const received = (ticket.history || [])
    .filter((h) => h.type === 'email_received')
    .map((h) => timeOf(h.at))
    .filter((t) => !Number.isNaN(t));
  for (const at of received) {
    let best = -1;
    let bestDist = Infinity;
    thread.forEach((m, i) => {
      if (flags[i] || (m.authorType !== 'associate' && m.authorType !== 'guest')) return;
      const dist = Math.abs(timeOf(m.sentAt) - at);
      if (dist <= EMAIL_MATCH_MS && dist < bestDist) {
        best = i;
        bestDist = dist;
      }
    });
    if (best >= 0) flags[best] = true;
  }
  return flags;
};

/** Note interne nate da un'email di un operatore (`addSystemNote` del poller). */
export const isEmailNote = (authorName?: string): boolean => /^Email da /.test(authorName || '');
